import React from "react";
import { ScrollView, StyleSheet, View } from "react-native";

import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { getSafeUrl } from "src/util/functions";
import { useIsMobile } from "src/hooks/ui";
import { tModalComponentProps } from "src/redux/modals/types";

import { PdStack } from "src/components/layout";
import { FormProvider, useForm } from "react-hook-form";
import { PdFormLabel } from "src/components/form-control";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdHookFormErrorMessage, PdHookFormInput } from "src/components/hook-form";
import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";

type tAddLinkFormData = {
  text: string;
  url: string;
};

const AddLinkModal: React.FC<tModalComponentProps<unknown, tAddLinkFormData>> = ({ onClose, onResolve }) => {
  const isMobile = useIsMobile();
  const formMethods = useForm<tAddLinkFormData>({
    defaultValues: {
      text: "",
      url: "",
    },
  });

  const handleOnClose = () => {
    onClose();
  };

  const handleOnSubmit = formMethods.handleSubmit((formData) => {
    onResolve({ text: formData.text, url: getSafeUrl(formData.url) });
  });

  return (
    <PdModal style={styles.addLinkModal} onClose={handleOnClose}>
      <PdModalCloseButton onPress={handleOnClose} />
      <ScrollView contentContainerStyle={styles.addLinkModalBody}>
        <PdModalTitle style={styles.mb5}>Add link</PdModalTitle>
        <FormProvider {...formMethods}>
          <View style={[styles.widthFull, styles.mb5]}>
            <PdFormLabel>Text</PdFormLabel>
            <PdHookFormInput name="text" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
            <PdHookFormErrorMessage name="text" />
          </View>
          <View style={[styles.widthFull, styles.mb5]}>
            <PdFormLabel>Link</PdFormLabel>
            <PdHookFormInput name="url" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
            <PdHookFormErrorMessage name="url" />
          </View>
          <PdStack
            isReversed={isMobile}
            direction={isMobile ? "vertical" : "horizontal"}
            layout={[1, 1]}
            gap={3}
            style={styles.widthFull}
          >
            <PdButton colorScheme="purple" variant="outline" onPress={handleOnClose}>
              <PdButtonLabel>Cancel</PdButtonLabel>
            </PdButton>
            <PdButton onPress={handleOnSubmit}>
              <PdButtonLabel>Add</PdButtonLabel>
            </PdButton>
          </PdStack>
        </FormProvider>
      </ScrollView>
    </PdModal>
  );
};

export const useAddLinkModal = (): tUseModal<unknown, tAddLinkFormData> => {
  return useModal<unknown, tAddLinkFormData>(AddLinkModal);
};

const styles = StyleSheet.create({
  addLinkModal: {
    marginHorizontal: 8 * kStyleConst.kSpacing,
    maxWidth: 400,
    width: "100%",
  },
  addLinkModalBody: {
    alignItems: "flex-start",
    backgroundColor: kColors.lightBrand,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  mb5: {
    marginBottom: kStyleConst.kSpacing * 5,
  },
  widthFull: {
    width: "100%",
  },
});
